/**
 * Helper: General
 * -----------------------------------------------------------------------------
 * General helper utility functions.
 * - Functions which don't fit into any of the other utils files.
 * - Add in alphabetical order.
 *
 */
import classes from '~/config/classes'

/**
 * Debounce functions.
 * - Calls a function when a user hasn't carried out an event for a specific
 *   amount of time.
 * @param {Function} callback The function to debounce.
 * @param {Number} wait - The amount of time before debounce call is triggered.
 * @param {Boolean} immediate - Trigger the immediately.
 */
export function debounce(callback, wait, immediate) {
  let timeout = null

  return function debounceFunc(...args) {
    const callNow = immediate && !timeout

    function later() {
      timeout = null
      if (!immediate) {
        callback.apply(this, args)
      }
    }

    window.clearTimeout(timeout)
    timeout = window.setTimeout(later, wait)

    if (callNow) {
      callback.apply(this, args)
    }
  }
}

/**
 * Handles pluralisation of strings based on Unicode plural rules.
 * - https://unicode-org.github.io/cldr-staging/charts/latest/supplemental/language_plural_rules.html
 * - Follows English plural rules.
 * - Zero + (One|Single) + (Two + Few) + (Other|Many|Plural).
 * @param {Object} strings - Strings object containing locale strings.
 * @param {Number} number - Number to use for plural rules.
 * @returns {String}
 */
export function handlePluralisation(strings, number) {
  if (strings.zero && number === 0) {
    return strings.zero
  }

  const one = strings.one || strings.single

  if (one && number === 1) {
    return one
  }

  if (strings.two && number === 2) {
    return strings.two
  }

  const many = strings.other || strings.many || strings.plural

  if (strings.few) {
    if (many && strings.two && number === 3) {
      return strings.few
    }

    if (!many && strings.two && number > 2) {
      return strings.few
    }

    if (!many && number > 1) {
      return strings.few
    }
  }

  return many
}

/**
 * Test if a URL is a Shopify CDN URL.
 * - Returns true if URL contains:
 * -- cdn.shopify.com/
 * -- [custom-domain].[tld]/cdn/
 * -- [store].myshopify.com/cdn/
 * @param {String} url - URL to test.
 * @returns {Boolean}
 */
export function isCdnUrl(url) {
  const cdnUrls = [
    'cdn.shopify.com/',
    `${cnvs.store.shopDomain.replaceAll('/', '')}/cdn/`,
    `${cnvs.store.permanentDomain.replaceAll('/', '')}/cdn/`,
  ]

  return cdnUrls.some((cdnUrl) => url.includes(cdnUrl))
}

/**
 * Detects if a part of an element is in the viewport.
 * @param {HTMLElement} element - Element to check.
 * @returns {Boolean} - True if element is at least partially in viewport.
 */
export function isVisible(element) {
  const windowHeight = window.innerHeight

  const elementRectangle = element.getBoundingClientRect()
  const elementTop = elementRectangle.top
  const elementBottom = elementRectangle.bottom

  const topInView = (elementTop > 0) && (elementTop < windowHeight)
  const bottomInView = (elementBottom > 0) && (elementBottom < windowHeight)

  return topInView || bottomInView
}

/**
 * Get multi-store form action.
 * @param {Object} instance - Selected store instance settings.
 * @returns {String}
 */
export function multiStoreAction(instance) {
  if (instance?.url) {
    return `${instance.url}localization`
  }

  return '/localization'
}

/**
 * Get multi-store return URL including search parameters and hash.
 * - Adds redirect URL parameters when store has instance settings.
 * @param {Object} store - Selected/suggested store settings.
 * @returns {String}
 */
export function multiStoreReturnTo(store) {
  let currentUrl = location.pathname
  const urlParameters = new URLSearchParams(location.search);

  /**
   * Remove existing country and language parameters.
   * - Also remove un-desired parameters.
   */
  ['_ab', '_fd', '_sc', 'country', 'language', 'shpxid'].forEach((parameter) => {
    urlParameters.delete(parameter)
  })

  if (!store || !store.country || !store.language) {
    currentUrl += urlParametersToString(urlParameters)
    currentUrl += location.hash
    return currentUrl
  }

  /**
   * Remove locale folder from URL.
   */
  if (cnvs.routes.root !== '/' && currentUrl.startsWith(cnvs.routes.root)) {
    currentUrl = `/${currentUrl.split(cnvs.routes.root)[1]}`
    currentUrl = currentUrl.replace('//', '/')
  }

  /**
   * Remove prefixed '/' when instance URL has value.
   * - Append redirect URL parameters.
   */
  if (store.instance?.url) {
    const formattedUrl = currentUrl.slice(0, 1) === '/'
      ? currentUrl.slice(1)
      : currentUrl

    currentUrl = `${store.instance.url}${formattedUrl}`

    urlParameters.append('country', store.country.code)
    urlParameters.append('language', store.language.code)
  }

  currentUrl += urlParametersToString(urlParameters)
  currentUrl += location.hash
  return currentUrl
}

/**
 * Converts URL parameters to string without changing encoding.
 * - Resolves issue where parameter value with a space is changed to +.
 * - Prefixes string with '?' if it has length.
 * @param {Object} parameters - URL search parameters.
 * @returns {String}
 */
function urlParametersToString(parameters) {
  const strings = []

  parameters.forEach((value, key) => {
    strings.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
  })

  return strings.length ? `?${strings.join('&')}` : ''
}

/**
 * Move asynchronous element after it's rendered.
 * @param {String} selector - Element selector.
 * @param {HTMLElement} target - Target destination.
 */
export function moveAsyncElement(selector, target) {
  const moveInterval = setInterval(() => {
    const container = document.querySelector(selector)

    if (!container) {
      return
    }

    clearInterval(moveInterval)
    target.appendChild(container)
    container.classList.remove(classes.hidden)
  }, 500)
}

/**
 * Scrolls page smoothly to element.
 * @param {HTMLElement} element - Element to scroll to.
 * @param {Object} data - Additional data.
 * @param {Function} data.callback - Function run after completion.
 * @param {String} data.headerState - State of header when function is run.
 * @param {HTMLElement} data.page - Element to scroll.
 */
export function scrollToElement(element, { callback, headerState, page = window } = {}) {
  let viewportFromPageTop = window.pageYOffset

  if (page !== window) {
    viewportFromPageTop = 0
  }

  /**
   * Get element scroll position.
   */
  const elementRectangle = element.getBoundingClientRect()
  const elementFromViewportTop = Math.floor(elementRectangle.top)
  let position = viewportFromPageTop + elementFromViewportTop

  /**
   * Retrieve header heights.
   */
  let headerHeight = getComputedStyle(document.documentElement)
    .getPropertyValue('--header-height')
    .replaceAll('px', '')

  headerHeight = Number(headerHeight)
  let brandNavigationHeight = 0

  if (cnvs.template.suffix === 'brand-directory') {
    brandNavigationHeight = getComputedStyle(document.documentElement)
      .getPropertyValue('--brand-navigation-height')
      .replaceAll('px', '')

    brandNavigationHeight = Number(brandNavigationHeight)
  }

  /**
   * Offset height of header and update position.
   */
  const threshold = headerState === 'collapsed'
    ? brandNavigationHeight
    : headerHeight + brandNavigationHeight

  let dynamicHeaderHeight = elementFromViewportTop > threshold
    ? brandNavigationHeight
    : headerHeight + brandNavigationHeight

  if (elementFromViewportTop === threshold && headerState === 'collapsed') {
    dynamicHeaderHeight = brandNavigationHeight
  }

  position -= dynamicHeaderHeight

  /**
   * Disable smooth scroll if prefers reduced motion.
   */
  const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ? 'auto'
    : 'smooth'

  /**
   * Run scroll.
   */
  page.scroll({
    behavior,
    left: 0,
    top: position,
  })

  if (typeof callback === 'function') {
    callback()
  }
}

/**
 * Set intersection observers to detect when add to cart button leaves viewport.
 * @param {Object} data - Data object.
 * @param {Object} data.callback - Function to run when intersecting.
 * @param {HTMLElement} data.element - Element to set observer on.
 * unobserved, must be a Vue element.
 * @param {String} [data.rootMargin] - Values to grow or shrink each side of the
 * root element's bounding box before computing intersections.
 * @param {Number|Array} [data.threshold] - Intersection observer threshold, 0.5
 * means the intersection is triggered when 50% of the element is in view.
 * @returns {Function}
 */
export function setIntersectionObserver({
  callback,
  element,
  rootMargin = '0px',
  threshold = 0.5,
}) {
  const options = {
    root: null,
    rootMargin,
    threshold,
  }

  const observer = new IntersectionObserver(callback, options)
  observer.observe(element)
}

/**
 * Throttle functions.
 * - Calls a function at intervals of a specified amount of time while the user
 *   is still carrying out the event.
 * @param {Function} callback - The function to execute when timer is passed.
 * @param {Number} wait - The amount of time before debounce call is triggered.
 * @param {Boolean} immediate - Trigger the immediately.
 */
export function throttle(callback, wait, immediate = false) {
  let timeout = null
  let initialCall = true

  return function throttleFunc(...args) {
    const callNow = immediate && initialCall

    function next() {
      callback.apply(this, args)
      timeout = null
    }

    if (callNow) {
      initialCall = false
      next()
    }

    if (!timeout) {
      timeout = window.setTimeout(next, wait)
    }
  }
}

/**
 * Wait for a window variable to be defined before proceeding with callback.
 * @example waitFor('nosto', callback)
 * @param {String} variable - Named window variable to wait for.
 * @param {Function} callback -Function to execute when interval is cleared.
 */
export function waitFor(variable, callback, options = {}) {
  const {
    timeout = 10000,
    pollRate = 100,
  } = options

  const startTime = performance.now()

  function check() {
    if (window[variable]) {
      callback()
      return
    }

    const elapsed = performance.now() - startTime
    if (elapsed >= timeout) {
      console.error(`waitFor Error: ${variable} not loaded after ${timeout}ms.`)
      return
    }

    setTimeout(check, pollRate)
  }

  check()
}
